import { useMutation, useQueryClient, QueryKey, UseMutationOptions } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import { logger } from "@/lib/logger";
type ToastType = "sonner";

interface UseSupabaseMutationOptions<TData, TVariables> {
  /**
   * The async function that performs the Supabase operation.
   * Should throw an error on failure.
   */
  mutationFn: (variables: TVariables) => Promise<TData>;

  /**
   * Query keys to invalidate on success.
   * Can be a single key or an array of keys.
   */
  invalidateKeys?: QueryKey | QueryKey[];

  /**
   * Success message to show in toast.
   * If not provided, no success toast is shown.
   */
  successMessage?: string;

  /**
   * Error message to show in toast.
   * Defaults to 'Ein Fehler ist aufgetreten'.
   */
  errorMessage?: string;

  /**
   * Whether to show detailed error message from the error object.
   * Defaults to false.
   */
  showErrorDetails?: boolean;

  /**
   * Which toast library to use.
   * Defaults to 'sonner'.
   */
  toastType?: ToastType;
  /**
   * Additional callback on success (after cache invalidation).
   */
  onSuccess?: (data: TData, variables: TVariables) => void;

  /**
   * Additional callback on error (after error toast).
   */
  onError?: (error: Error, variables: TVariables) => void;

  /**
   * Additional mutation options to pass to useMutation.
   */
  mutationOptions?: Omit<
    UseMutationOptions<TData, Error, TVariables>,
    "mutationFn" | "onSuccess" | "onError"
  >;
}

/**
 * A generic wrapper around TanStack Query's useMutation for Supabase operations.
 *
 * Reduces CRUD boilerplate by handling:
 * - Toast notifications (success and error)
 * - Query cache invalidation
 * - Error logging
 *
 * @example
 * ```typescript
 * const deleteChecklist = useSupabaseMutation({
 *   mutationFn: async (id: string) => {
 *     const { error } = await supabase.from('checklists').delete().eq('id', id);
 *     if (error) throw error;
 *   },
 *   invalidateKeys: checklistsKeys.all,
 *   successMessage: t('checklists.deleted'),
 *   errorMessage: t('checklists.deleteError'),
 *   toastType: 'sonner',
 * });
 *
 * // Usage
 * deleteChecklist.mutate(checklistId);
 * ```
 */
export function useSupabaseMutation<TData = void, TVariables = void>({
  mutationFn,
  invalidateKeys,
  successMessage,
  errorMessage = "Ein Fehler ist aufgetreten",
  showErrorDetails = false,
  toastType = "sonner",
  onSuccess: onSuccessCallback,
  onError: onErrorCallback,
  mutationOptions,
}: UseSupabaseMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();

  const showSuccessToast = (message: string) => {
    sonnerToast.success(message);
  };

  const showErrorToast = (message: string, details?: string) => {
    sonnerToast.error(message, { description: details });
  };

  return useMutation({
    mutationFn,
    onSuccess: async (data, variables) => {
      // Invalidate query caches
      if (invalidateKeys) {
        const keys = Array.isArray(invalidateKeys[0])
          ? (invalidateKeys as QueryKey[])
          : [invalidateKeys as QueryKey];

        await Promise.all(keys.map((key) => queryClient.invalidateQueries({ queryKey: key })));
      }

      // Show success toast
      if (successMessage) {
        showSuccessToast(successMessage);
      }

      // Call additional success callback
      onSuccessCallback?.(data, variables);
    },
    onError: (error: Error, variables) => {
      logger.error("Mutation error:", error);

      // Show error toast
      const details = showErrorDetails ? error.message : undefined;
      showErrorToast(errorMessage, details);

      // Call additional error callback
      onErrorCallback?.(error, variables);
    },
    ...mutationOptions,
  });
}
